<script lang="ts">
export default {
  name: 'AccountProgramContact',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { Mail } from 'lucide-vue-next';
import { AccountContact, ProgramContact } from '@/types/AccountContact';

interface GroupedAccountContacts {
  serviceTitle: string;
  contacts: AccountContact[];
}

const props = defineProps<{
  programContact: ProgramContact;
  expand: boolean;
}>();

const groupedContacts = computed(() => {
  // TODO: the program contacts transform logic will need to be reworked and simplified.
  // this complicated mess of a solution is to address the usage of the serviceTitle property to group the contacts.
  // The roles array actually contains the role associated with the array of contacts. This is most likely a side effect
  // from repurposing an interface which doesn't represent the structure of the expected incoming data.
  const contacts = props.programContact.contacts.map((c) => ({ ...c, contacts: c.contacts.map((cc) => ({ ...cc, serviceTitle: c.roles[0] })) }));

  const allContacts = contacts.flatMap((row) => row.contacts);

  const grouped = allContacts.reduce((acc, c) => {
    if (!acc.has(c.serviceTitle)) acc.set(c.serviceTitle, []);
    acc.get(c.serviceTitle)?.push(c);
    return acc;
  }, new Map<string, AccountContact[]>());

  const orderedContacts: GroupedAccountContacts[] = [];
  for (const [serviceTitle, contacts] of grouped) {
    orderedContacts.push({ serviceTitle, contacts } as GroupedAccountContacts);
  }

  return orderedContacts;
});
</script>

<template>
  <div class="tw:flex tw:w-full">
    <div>
      <div
        v-for="group in groupedContacts"
        :key="group.serviceTitle"
        class="contact-tuple tw:mb-2">
        <div class="tw:text-xs tw:font-bold tw:text-gray-500 tw:uppercase">{{ group.serviceTitle }}:</div>
        <div
          v-for="contact in group.contacts"
          :key="contact.email">
          <span class="contact tw:pr-5 tw:wrap-break-word">
            <a
              :href="`mailto:${contact.email}`"
              class="tw:pl-1 tw:wrap-break-word">
              <mail
                class="tw:inline tw:h-4 tw:w-4 tw:transform tw:text-gray-400 tw:transition-all tw:duration-200 tw:hover:scale-110 tw:hover:text-red-500" />
            </a>
            {{ contact.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
